import React, { useState } from 'react';
import { Card, Radio, Button, Modal, message, Space, Image, Typography } from 'antd';
import { AlipayCircleOutlined, CreditCardOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import Navbar from '../components/layout/Navbar';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser, setUser } from '../store/userSlice';
import { auth } from '../services/api';
import xiaohongshuQR from '../assets/xiaohongshu.png';

const { Title } = Typography;

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
  padding-top: 64px;
`;

const ContentContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
  
  h2 {
    margin-bottom: 16px !important;
  }
  
  .subtitle {
    font-size: 16px;
    color: #666;
    max-width: 600px;
    margin: 0 auto;
  }
`;

const BenefitsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 60px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  text-align: center;
  
  .icon {
    font-size: 32px;
    color: #1890ff;
    margin-bottom: 16px;
  }
  
  h4 {
    margin: 0 0 12px;
    font-size: 18px;
    font-weight: 600;
  }
  
  p {
    color: #666;
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
  }
`;

const PricingCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  background: linear-gradient(to bottom, #ffffff, #f8fafc);
  
  .ant-card-body {
    padding: 32px;
    display: flex;
    gap: 40px;

    @media (max-width: 768px) {
      flex-direction: column;
    }
  }
`;

const PricingInfo = styled.div`
  flex: 1;

  .title-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 16px;

    .ant-typography {
      margin: 0;
      font-size: 24px;
      font-weight: 600;
    }
  }

  .discount-badge {
    background: #ff4d4f;
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
  }
`;

const PaymentInfo = styled.div`
  flex: 1;
  padding-left: 40px;
  border-left: 1px solid #f0f0f0;

  @media (max-width: 768px) {
    padding-left: 0;
    padding-top: 24px;
    border-left: none;
    border-top: 1px solid #f0f0f0;
  }
`;

const PaymentOption = styled(Radio.Button)`
  height: 50px;
  line-height: 48px;
  padding: 0 24px;
  font-size: 16px;
  
  .anticon {
    font-size: 20px;
    margin-right: 8px;
    vertical-align: -2px;
  }
`;

const Price = styled.div`
  text-align: center;
  margin: 24px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  
  .original-price {
    color: #999;
    font-size: 20px;
    text-decoration: line-through;
  }
  
  .price {
    font-size: 36px;
    font-weight: 600;
    color: #1890ff;
    
    small {
      font-size: 16px;
      font-weight: normal;
      color: #666;
      margin-left: 4px;
    }
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 24px auto;
  text-align: center;
  max-width: 200px;
  
  li {
    margin: 12px 0;
    padding-left: 24px;
    position: relative;
    text-align: left;
    
    &:before {
      content: "✓";
      position: absolute;
      left: 0;
      color: #52c41a;
    }
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
    overflow: hidden;
  }
  
  .ant-modal-body {
    padding: 32px;
    text-align: center;
  }
  
  img {
    max-width: 200px;
    margin: 20px 0;
  }
`;

const TestimonialSection = styled.div`
  margin: 60px 0;
  text-align: center;
  
  h3 {
    margin-bottom: 32px;
    font-size: 24px;
    font-weight: 600;
  }
  
  .testimonials {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
`;

const TestimonialCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  
  .quote {
    font-style: italic;
    color: #333;
    margin-bottom: 16px;
    line-height: 1.6;
  }
  
  .author {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    
    .avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: #f0f0f0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      color: #666;
    }
    
    .info {
      text-align: left;
      
      .name {
        font-weight: 600;
        margin-bottom: 4px;
      }
      
      .role {
        font-size: 12px;
        color: #666;
      }
    }
  }
`;

const FAQSection = styled.div`
  margin: 60px 0;
  
  h3 {
    text-align: center;
    margin-bottom: 32px;
    font-size: 24px;
    font-weight: 600;
  }
`;

const PricingView: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [paymentMethod, setPaymentMethod] = useState<string>('alipay');
  const [modalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    if (!user) {
      message.info('请先登录');
      navigate('/auth');
      return;
    }

    // 如果用户已经是会员，不执行任何操作
    if (user.isPremium) {
      return;
    }

    setLoading(true);
    try {
      // // 调用后端API添加一个月会员
      // await auth.addTrialMembership();
      // // 获取最新的用户信息并更新 Redux store
      // const updatedUser = await auth.getCurrentUser();
      // dispatch(setUser(updatedUser));
      setModalVisible(true);
    } catch (error) {
      message.error('操作失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 获取按钮文本和禁用状态
  const getButtonProps = () => {
    if (!user) {
      return {
        text: '立即升级',
        disabled: false
      };
    }
    if (user.isPremium) {
      return {
        text: '续费',
        disabled: true
      };
    }
    return {
      text: '立即升级',
      disabled: false
    };
  };

  const buttonProps = getButtonProps();

  return (
    <>
      <Navbar />
      <PageContainer>
        <ContentContainer>
          <HeaderSection>
            <Title level={2}>升级专业版，让创业更轻松</Title>
            <p className="subtitle">
              获取完整的AI创业助手功能，让我们帮助你更快地实现创业目标
            </p>
          </HeaderSection>

          <PricingCard>
            <PricingInfo>
              <div className="title-wrapper">
                <Typography.Title level={3}>专业版</Typography.Title>
                <div className="discount-badge">限时0折</div>
              </div>
              <Price>
                <div className="original-price">¥99/月</div>
                <div className="price">
                  ¥0<small>/月</small>
                </div>
              </Price>
              <FeatureList>
                <li>可创建多个项目</li>
                <li>AI 全程辅助创业</li>
                <li>进入创业者社群</li>
                <li>新功能优先体验</li>
              </FeatureList>
            </PricingInfo>
            <PaymentInfo>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Radio.Group 
                  value={paymentMethod} 
                  onChange={e => setPaymentMethod(e.target.value)}
                  style={{ marginBottom: 24 }}
                  disabled={user?.isPremium}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <PaymentOption value="alipay">
                      <AlipayCircleOutlined style={{ color: '#1677FF' }} /> 支付宝
                    </PaymentOption>
                    <PaymentOption value="paypal">
                      <CreditCardOutlined style={{ color: '#0070BA' }} /> PayPal
                    </PaymentOption>
                  </Space>
                </Radio.Group>
                <Button 
                  type="primary" 
                  size="large" 
                  block 
                  onClick={handleUpgrade} 
                  loading={loading}
                  disabled={buttonProps.disabled}
                >
                  {buttonProps.text}
                </Button>
                {user?.isPremium && user?.premiumExpireAt && (
                  <div style={{ textAlign: 'center', marginTop: 12, color: '#666' }}>
                    会员有效期至：{new Date(user.premiumExpireAt).toLocaleDateString()}
                  </div>
                )}
              </Space>
            </PaymentInfo>
          </PricingCard>

          <FAQSection>
            <h3>常见问题</h3>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Card>
                <h4>如何开始使用？</h4>
                <p>注册账号后即可免费体验基础功能，升级专业版后可使用全部功能。</p>
              </Card>
              <Card>
                <h4>支持哪些支付方式？</h4>
                <p>目前暂未开放支付功能，后续会陆续开放更多支付方式。</p>
              </Card>
              <Card>
                <h4>提供创业咨询服务吗？</h4>
                <p>加入创业者社群可以相互探讨、交换信息，社群内会提供付费咨询渠道。</p>
              </Card>
            </Space>
          </FAQSection>
        </ContentContainer>
      </PageContainer>

      <StyledModal
        open={modalVisible}
        footer={null}
        onCancel={() => {
          setModalVisible(false);
          navigate('/app');
        }}
        width={400}
      >
        <h3>🙏 感谢支持！</h3>
        <p>付费功能暂未开通</p>
        <Image src={xiaohongshuQR} alt="小红书二维码" />
        <p>扫码加入小红书群聊，获取最新进展</p>
        <Button type="primary" onClick={() => {
          setModalVisible(false);
          navigate('/app');
        }}>
          开始使用
        </Button>
      </StyledModal>
    </>
  );
};

export default PricingView; 